import React from 'react'

import { Button, Col, Table, Row } from '_zari/core'

import { OrigenesContabilidadConstant } from 'constants/index'
import * as AsientosContablesService from 'services/asientosContables.service'

import * as Modals from './modals'

import RowTemplate from './rowTemplate'

const tableHeaderRows = [
  { label: 'CUENTA CONTABLE' },
  { label: 'CENTRO COSTO 1', width: 170 },
  { label: 'CENTRO COSTO 2', width: 170 },
  { label: 'DESTINO', width: 170 },
  { label: 'MONEDA', width: 100 },
  { label: 'DEBE', width: 90 },
  { label: 'HABER', width: 90 },
  { label: '', width: 50 },
  { label: '', width: 30 }
]

const DetalleTablaSection = ({
  setDiferenciaSoles,
  vTipoCambio,
  vOrigen,
  vGlosa,
  vIdGlosa,
  detalles,
  dispatch,
  setFieldValue,
  isLargeDataset = false,
  loadMore,
  isLoadingMore = false,
  hasMore = false,
  totalRegistros = 0,
  asientoId = null,
  registrarModificacion,
  registrarEliminacion
}) => {
  const [mostrarEntregaARendirModal, setMostrarEntregaARendirModal] = React.useState(false)
  const [isExporting, setIsExporting] = React.useState(false)

  const loaderRef = React.useRef(null)

  React.useEffect(() => {
    if (!isLargeDataset || !loadMore) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMore()
        }
      },
      { threshold: 0.1 }
    )

    const currentLoader = loaderRef.current
    if (currentLoader) {
      observer.observe(currentLoader)
    }

    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader)
      }
    }
  }, [isLargeDataset, loadMore, hasMore, isLoadingMore])

  const updateStatus = React.useCallback(
    payload => {
      setDiferenciaSoles(0)
      dispatch({ type: 'actualizar', payload: payload })
      if (registrarModificacion) {
        registrarModificacion(payload)
      }
    },
    [dispatch, setDiferenciaSoles, registrarModificacion]
  )

  const clickAgregarFila = React.useCallback(() => {
    dispatch({
      type: 'agregar',
      payload: { tipoCambio: vTipoCambio, glosa: vGlosa, _idGlosa: vIdGlosa }
    })
  }, [dispatch, vTipoCambio, vGlosa, vIdGlosa])

  const handleEliminar = React.useCallback(
    detalle => {
      dispatch({ type: 'eliminar', payload: { _idFila: detalle._idFila } })
      if (registrarEliminacion) {
        registrarEliminacion(detalle)
      }
    },
    [dispatch, registrarEliminacion]
  )

  const clickEntregasARendir = React.useCallback(() => {
    setMostrarEntregaARendirModal(true)
  }, [])

  const clickExportarExcel = React.useCallback(() => {
    if (!asientoId) return

    setIsExporting(true)
    AsientosContablesService.exportarExcelDetalles(asientoId)
      .then(({ data }) => {
        const url = window.URL.createObjectURL(new Blob([data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `asiento-${asientoId}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      })
      .catch(error => {
        console.error('Error al exportar el archivo Excel', error)
      })
      .finally(() => {
        setIsExporting(false)
      })
  }, [asientoId])

  return (
    <>
      <Row className='mb-2'>
        <Col md={6}>
          <Button id='button-agregarFila' onClick={clickAgregarFila} tiny>
            Agregar Fila
          </Button>
          {isLargeDataset && asientoId && (
            <Button onClick={clickExportarExcel} tiny outline className='ml-2' disabled={isExporting}>
              {isExporting ? 'Exportando...' : 'Exportar Excel'}
            </Button>
          )}
          {isLargeDataset && totalRegistros > 0 && (
            <span className='ml-3' style={{ fontSize: '12px', color: '#666' }}>
              Mostrando {detalles.length} de {totalRegistros} registros
            </span>
          )}
        </Col>
        {vOrigen === OrigenesContabilidadConstant.CAJA_EGRESOS || vOrigen === OrigenesContabilidadConstant.BANCOS ? (
          <Col md={6} className='text-right'>
            <Button onClick={clickEntregasARendir} tiny outline>
              Entregas a rendir
            </Button>
          </Col>
        ) : null}
      </Row>
      <div style={{ maxHeight: '400px', overflow: 'auto' }}>
        <Table
          containerId='container-detallesnuevoasiento'
          filas={detalles}
          headRows={tableHeaderRows}
          rowTemplate={(row, k) => (
            <RowTemplate
              key={row._idFila}
              row={row}
              rowIndex={k}
              updateStatus={updateStatus}
              handleEliminar={handleEliminar}
              handleAgregarFila={clickAgregarFila}
              esUltimaFila={k === detalles.length - 1}
            />
          )}
        />
        {isLargeDataset && (
          <div ref={loaderRef} style={{ padding: '10px', textAlign: 'center' }}>
            {isLoadingMore && <span>Cargando más registros...</span>}
            {!hasMore && detalles.length > 0 && (
              <span style={{ color: '#666', fontSize: '12px' }}>Todos los registros cargados</span>
            )}
          </div>
        )}
      </div>
      {mostrarEntregaARendirModal && (
        <Modals.EntregaARendirModal
          show={mostrarEntregaARendirModal}
          handleClose={() => setMostrarEntregaARendirModal(false)}
          dispatch={dispatch}
          vTipoCambio={vTipoCambio}
          setFieldValue={setFieldValue}
        />
      )}
    </>
  )
}

export default React.memo(DetalleTablaSection)
